import { useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { getJson, postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { Counter, DataTable, MobileActions, RowMenu, RowsSkeleton, Window, useIsMobile } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { ListFooter, Pager, useServerList } from "../ui/list";
import { useSearchParams } from "react-router";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts, type Label } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, OptionCards, PageHead, TextField, useToast } from "../ui/kit";
import { PageState } from "../ui/pieces";
import {
  audienceKey,
  audiences,
  canDecideVersion,
  canRetryIndex,
  canRetryVersion,
  isProcessing,
  orgRoles,
  processingKey,
  runningKey,
  sourceTypeKey,
  termKindKey,
  type Classification,
  type OrgHead,
  type Term,
  type Version,
  permissionOptions,
  permissionsSummary,
  type PermissionEntry,
} from "./employees";
import { BlockFailed, EmployeesHead, Failed, TermCheck, replyMessage } from "./EmployeesPages";
import { plural, roleKey, whenIn } from "./organizations";
import { Check, ConfirmWindow } from "./PersonActions";
import { SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";
import own from "./employees.module.css";

// R5 · Empresa › Empleados IA › Conocimiento (§5.6): bases (lista y ficha con Fuentes · Clasificación), fuentes (ficha
// con Contenido · Versiones · Clasificación y alta) y términos, con sus ventanas (§6). La clasificación de la base y la
// de la fuente son el mismo formulario (E48) y guardan sin borrar lo que no muestran (B6, H-2).

type Base = { id: number; name: string; description: string | null; active: boolean };

const activeText = (L: Label, active: boolean) => L(active ? "ADM_KB_ACTIVE" : "ADM_KB_INACTIVE");

export const baseHref = (orgId: number, baseId: number, tab: "fuentes" | "clasificacion" = "fuentes") =>
  `/Organizations/ConocimientoDetalle?id=${orgId}&baseId=${baseId}&tab=${tab}`;

export const sourceHref = (
  orgId: number,
  sourceId: number,
  tab: "contenido" | "versiones" | "clasificacion" = "contenido",
) => `/Organizations/ConocimientoFuente?id=${orgId}&sourceId=${sourceId}&tab=${tab}`;

/**
 * Activar (al instante con «Deshacer») y desactivar (confirma, D8) una base o una fuente. `kind` elige la ruta y los
 * textos; la confirmación dice qué pasa.
 */
function useKnowledgeStatus(orgId: number, kind: "base" | "source", onChanged: () => void) {
  const { L } = useTexts();
  const toast = useToast();
  const [confirming, setConfirming] = useState<{ id: number; name: string } | null>(null);
  const path = kind === "base" ? "knowledge-bases" : "knowledge-sources";
  const set = async (item: { id: number; name: string }, active: boolean, undo = true): Promise<boolean> => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/${path}/${item.id}/status`,
      { status: active ? "ACTIVE" : "INACTIVE" },
    );
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(replyMessage(L, reply.body));
      return false;
    }
    onChanged();
    toast.show(
      formatText(L(active ? "ADM_KB_ACTIVATED" : "ADM_KB_DEACTIVATED"), item.name),
      undo ? { label: L("ADM_UNDO"), run: () => void set(item, !active, false) } : undefined,
    );
    return true;
  };
  return {
    activate: (item: { id: number; name: string }) => void set(item, true),
    deactivate: (item: { id: number; name: string }) => setConfirming(item),
    window: (
      <ConfirmWindow
        open={!!confirming}
        title={formatText(L("ADM_KB_DEACTIVATE_TITLE"), confirming?.name ?? "")}
        line={L(kind === "base" ? "ADM_KB_DEACTIVATE_BASE_LINE" : "ADM_KB_DEACTIVATE_SOURCE_LINE")}
        action={L("ADM_PPL_DEACTIVATE")}
        onClose={() => setConfirming(null)}
        onConfirm={() => (confirming ? set(confirming, false) : Promise.resolve(true))}
      />
    ),
  };
}

/** `base` y `fuente` · ventana con Nombre y Descripción (crear o editar). */
function NamedWindow({
  open,
  title,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  initial: { name: string; description: string };
  onClose: () => void;
  onSave: (name: string, description: string) => Promise<{ saved: boolean; error?: string; field?: boolean }>;
}) {
  const { L } = useTexts();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setName(initial.name);
      setDescription(initial.description);
      setError(null);
      setGeneral(null);
    }
  }
  const save = () => {
    if (!name.trim()) {
      setError(L("ORGKBF_ERR_NAME"));
      requestAnimationFrame(() => document.getElementById("named-name")?.focus());
      return;
    }
    setBusy(true);
    void onSave(name.trim(), description.trim()).then((result) => {
      setBusy(false);
      if (result.saved) onClose();
      else if (result.field) setError(result.error ?? null);
      else setGeneral(result.error ?? null);
    });
  };
  return (
    <Window
      open={open}
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button type="button" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined} onClick={save}>
            {L("ADM_PPL_SAVE")}
          </button>
        </>
      }
    >
      {general ? <Notice tone="error">{general}</Notice> : null}
      <TextField
        id="named-name"
        label={L("ADM_KB_NAME")}
        value={name}
        error={error}
        maxLength={200}
        onChange={(value) => {
          setName(value);
          if (value.trim()) setError(null);
        }}
      />
      <Field id="named-description" label={L("ADM_KB_DESCRIPTION")} optional={L("CFG1_R1_OPTIONAL")}>
        <textarea
          id="named-description"
          className={styles.textarea}
          value={description}
          maxLength={1000}
          rows={3}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
    </Window>
  );
}

type BaseRow = { id: number; name: string; active: boolean; description: string | null; sources: number };

/**
 * `emp-conocimiento` · Empleados IA › Conocimiento: tabla C28 Base · Estado con ⋮ Editar… (ventana `base`) y
 * Desactivar… (C12) o Activar (C13); «Nueva base» abre la ventana; «Términos» lleva a Términos de clasificación.
 */
export function KnowledgePage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  // R7 · B8 + B11 (AR13, AR14): paginada en el servidor; cada fila trae su descripción y «N fuentes» en la misma consulta.
  const list = useServerList<BaseRow>(`/api/admin/organizations/${orgId}/knowledge-bases`);
  const status = useKnowledgeStatus(orgId, "base", () => list.reload());
  const [editing, setEditing] = useState<{ id: number | null; name: string; description: string } | null>(null);
  useOpenOrg((list.extra?.organization as OrgHead | undefined) ?? null);
  useTitle(`${L("ADM_ORG_EMPLOYEES")} · ${L("ADM_EMP_TAB_KNOWLEDGE")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const termsHref = `/Organizations/ConocimientoTerminos?id=${orgId}`;
  const openNew = () => setEditing({ id: null, name: "", description: "" });
  const head = (
    <EmployeesHead orgId={orgId} current="knowledge">
      {/* Celular: «Términos» en el ⋮ y «+» redondo junto al título (C21). */}
      {mobile ? (
        <RowMenu
          label={L("ADM_MORE_ACTIONS")}
          items={[{ label: L("ADM_KB_TERMS"), onSelect: () => navigate(termsHref) }]}
        />
      ) : null}
      <a className={`tm-btn secondary ${ui.desktopInline}`} href={termsHref}>
        {L("ADM_KB_TERMS")}
      </a>
      <button type="button" className={`tm-btn primary ${ui.desktopInline}`} onClick={openNew}>
        {L("ADM_KB_NEW_BASE")}
      </button>
      <button
        type="button"
        className={ui.fab}
        aria-label={L("ADM_KB_NEW_BASE")}
        title={L("ADM_KB_NEW_BASE")}
        onClick={openNew}
      >
        <ChatIcon name="plus" />
      </button>
    </EmployeesHead>
  );
  const editWindow = (
    <NamedWindow
      open={!!editing}
      title={L("ADM_KB_BASE_WINDOW")}
      initial={{ name: editing?.name ?? "", description: editing?.description ?? "" }}
      onClose={() => setEditing(null)}
      onSave={async (name, description) => {
        const target = editing;
        const url = target?.id
          ? `/api/admin/organizations/${orgId}/knowledge-bases/${target.id}`
          : `/api/admin/organizations/${orgId}/knowledge-bases`;
        const reply = await postJson<{ saved?: boolean; baseId?: number; field?: string; messageKey?: string }>(url, {
          name,
          description,
        });
        if (reply.status === 200 && reply.body?.saved) {
          toast.show(L("ADM_KB_BASE_SAVED"));
          if (!target?.id && reply.body.baseId) navigate(baseHref(orgId, reply.body.baseId));
          else list.reload();
          return { saved: true };
        }
        return { saved: false, error: replyMessage(L, reply.body), field: reply.body?.field === "name" };
      }}
    />
  );
  /** Editar lee la base (su descripción no viene en la lista). */
  const edit = async (row: { id: number; name: string }) => {
    const reply = await getJson<{ base: Base }>(`/api/admin/organizations/${orgId}/knowledge-bases/${row.id}`);
    if (reply.status === 200 && reply.body)
      setEditing({ id: row.id, name: reply.body.base.name, description: reply.body.base.description ?? "" });
    else toast.show(L("ADMIN_ERR_GENERIC"));
  };
  if (list.state !== "ready") {
    if (list.state !== "loading")
      return <Failed head={head} status={list.state === "forbidden" ? 403 : 502} retry={list.reload} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  }
  /** «Políticas de cambios · 4 fuentes»: la descripción (si la hay) y cuántas fuentes tiene. */
  const baseLine = (row: BaseRow) =>
    [row.description, plural(L, locale, row.sources, "ADM_KB_SOURCES_ONE", "ADM_KB_SOURCES_N")]
      .filter(Boolean)
      .join(" · ");
  const menu = (row: { id: number; name: string; active: boolean }) => [
    { label: L("ADM_KB_EDIT_MENU"), onSelect: () => void edit(row) },
    row.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => status.deactivate(row) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => status.activate(row) },
  ];
  return (
    <>
      {head}
      {list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_KB_EMPTY")}</strong>
          <span>{L("ADM_KB_EMPTY_LINE")}</span>
          <button type="button" className="tm-btn primary" onClick={openNew}>
            {L("ADM_KB_NEW_BASE")}
          </button>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_KB_COUNT_ONE", "ADM_KB_COUNT")}</Counter>
          <div className={own.fit}>
            <DataTable
              label={L("ADM_EMP_TAB_KNOWLEDGE")}
              rows={list.items}
              rowKey={(row) => row.id}
              href={(row) => baseHref(orgId, row.id)}
              columns={[
                {
                  key: "name",
                  title: L("ADM_KB_BASE"),
                  render: (row) => (
                    <span className={ui.cellMain} title={row.name}>
                      <strong>{row.name}</strong>
                      <span className={`${ui.cellSub} ${ui.ellipsis}`}>{baseLine(row)}</span>
                    </span>
                  ),
                },
                { key: "status", title: L("ADM_KB_COL_STATUS"), render: (row) => activeText(L, row.active) },
                {
                  key: "menu",
                  title: "",
                  render: (row) => (
                    <RowMenu
                      label={formatText(L("ADM_PPL_ACTIONS_FOR"), row.name)}
                      title={row.name}
                      items={menu(row)}
                    />
                  ),
                },
              ]}
              mobile={(row) => ({ title: row.name, line: `${baseLine(row)} · ${activeText(L, row.active)}` })}
            />
          </div>
          <ListFooter list={list} />
        </>
      )}
      {editWindow}
      {status.window}
    </>
  );
}

type BaseRecord = {
  organization: OrgHead;
  base: Base;
  items: { id: number; name: string; type: string; active: boolean }[];
  total: number;
  page: number;
  pageSize: number;
  classification: Classification | null;
};

/**
 * `emp-base` y `emp-base-clasif` · ficha de la base: ← a Conocimiento, estado, descripción, «Editar» (ventana `base`),
 * ⋮ Desactivar…/Activar y las pestañas Fuentes · Clasificación en la URL.
 */
export function BasePage({ orgId, baseId, tab }: { orgId: number; baseId: number; tab: "fuentes" | "clasificacion" }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const [params] = useSearchParams();
  const pageQuery = new URLSearchParams();
  if (params.get("page")) pageQuery.set("page", params.get("page")!);
  if (params.get("pageSize")) pageQuery.set("pageSize", params.get("pageSize")!);
  const data = useAiData<BaseRecord>(
    `/api/admin/organizations/${orgId}/knowledge-bases/${baseId}${pageQuery.size ? `?${pageQuery}` : ""}`,
  );
  const reload = () => void data.reload();
  const status = useKnowledgeStatus(orgId, "base", reload);
  const sources = useKnowledgeStatus(orgId, "source", reload);
  const [editing, setEditing] = useState(false);
  useOpenOrg(data.data?.organization ?? null);
  const record = data.data;
  useTitle(
    `${record?.base.name ?? L("ADM_EMP_TAB_KNOWLEDGE")} · ${L(tab === "fuentes" ? "ADM_KB_SOURCES" : "ADM_KB_CLASSIFICATION")}`,
  );
  const back = { href: `/Organizations/Conocimiento?id=${orgId}`, label: L("ADM_EMP_TAB_KNOWLEDGE") };
  const tabs = (
    <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_KB_BASE_TABS")} data-part="pestanas">
      {(["fuentes", "clasificacion"] as const).map((value) => (
        <a key={value} href={baseHref(orgId, baseId, value)} aria-current={value === tab ? "page" : undefined}>
          {L(value === "fuentes" ? "ADM_KB_SOURCES" : "ADM_KB_CLASSIFICATION")}
        </a>
      ))}
    </nav>
  );
  if (data.state === "missing") return <KnowledgeMissing orgId={orgId} />;
  if (!record) {
    const head = (
      <>
        <PageHead title={L("ADM_EMP_TAB_KNOWLEDGE")} back={back} />
        {tabs}
      </>
    );
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={4} table={!mobile} />
      </>
    );
  }
  const base = record.base;
  const newSource = `/Organizations/ConocimientoFuenteNueva?id=${orgId}&baseId=${baseId}`;
  const editButton = (
    <button type="button" className={`tm-btn secondary ${ui.desktopInline}`} onClick={() => setEditing(true)}>
      {L("ADM_KB_EDIT")}
    </button>
  );
  const primary =
    tab === "fuentes" ? (
      <a className="tm-btn primary" href={newSource}>
        {L("ADM_KB_NEW_SOURCE")}
      </a>
    ) : record.classification ? (
      <button type="submit" form="classification" className="tm-btn primary">
        {L("ADM_PPL_SAVE")}
      </button>
    ) : null;
  const menu = [
    ...(mobile ? [{ label: L("ADM_KB_EDIT"), onSelect: () => setEditing(true) }] : []),
    base.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => status.deactivate(base) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => status.activate(base) },
  ];
  const sourceMenu = (row: { id: number; name: string; active: boolean }) => [
    row.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => sources.deactivate(row) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => sources.activate(row) },
  ];
  return (
    <>
      <div className={own.ficha}>
        <PageHead
          title={base.name}
          back={back}
          titleTag={<span className="tm-tag">{activeText(L, base.active)}</span>}
          sub={base.description ?? undefined}
        >
          <RowMenu label={L("ADM_MORE_ACTIONS")} title={base.name} items={menu} />
          {editButton}
          {primary ? <span className={ui.desktopInline}>{primary}</span> : null}
        </PageHead>
      </div>
      {tabs}
      {tab === "fuentes" ? (
        record.items.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_KB_NO_SOURCES")}</strong>
            <span>{L("ADM_KB_NO_SOURCES_LINE")}</span>
            <a className="tm-btn primary" href={newSource}>
              {L("ADM_KB_NEW_SOURCE")}
            </a>
          </div>
        ) : (
          <>
            <Counter>{plural(L, locale, record.total, "ADM_KB_SOURCES_ONE", "ADM_KB_SOURCES_N")}</Counter>
            <div className={own.fit}>
              <DataTable
                label={L("ADM_KB_SOURCES")}
                rows={record.items}
                rowKey={(row) => row.id}
                href={(row) => sourceHref(orgId, row.id)}
                columns={[
                  {
                    key: "name",
                    title: L("ADM_KB_COL_SOURCE"),
                    render: (row) => <span title={row.name}>{row.name}</span>,
                  },
                  {
                    key: "type",
                    title: L("ADM_KB_COL_TYPE"),
                    render: (row) => L(sourceTypeKey[row.type] ?? "ADM_KB_TYPE_TEXT"),
                  },
                  { key: "status", title: L("ADM_KB_COL_STATUS"), render: (row) => activeText(L, row.active) },
                  {
                    key: "menu",
                    title: "",
                    render: (row) => (
                      <RowMenu
                        label={formatText(L("ADM_PPL_ACTIONS_FOR"), row.name)}
                        title={row.name}
                        items={sourceMenu(row)}
                      />
                    ),
                  },
                ]}
                mobile={(row) => ({
                  title: row.name,
                  line: `${L(sourceTypeKey[row.type] ?? "ADM_KB_TYPE_TEXT")} · ${activeText(L, row.active)}`,
                })}
              />
            </div>
            {/* R7 · B8 (AR13): paginado del servidor (también en el celular, en esta ficha). */}
            <Pager total={record.total} page={record.page} pageSize={record.pageSize} />
          </>
        )
      ) : record.classification ? (
        <ClassificationForm
          key={JSON.stringify(record.classification)}
          url={`/api/admin/organizations/${orgId}/knowledge-bases/${baseId}/classification`}
          classification={record.classification}
          termsHref={`/Organizations/ConocimientoTerminos?id=${orgId}`}
          onSaved={() => {
            toast.show(L("ADM_KB_CLASSIFICATION_SAVED"));
            reload();
          }}
        />
      ) : (
        <BlockFailed retry={data.retry} />
      )}
      {primary ? <MobileActions>{primary}</MobileActions> : null}
      <NamedWindow
        open={editing}
        title={L("ADM_KB_BASE_WINDOW")}
        initial={{ name: base.name, description: base.description ?? "" }}
        onClose={() => setEditing(false)}
        onSave={async (name, description) => {
          const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/knowledge-bases/${baseId}`,
            { name, description },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(L("ADM_KB_BASE_SAVED"));
            reload();
            return { saved: true };
          }
          return { saved: false, error: replyMessage(L, reply.body), field: reply.body?.field === "name" };
        }}
      />
      {status.window}
      {sources.window}
    </>
  );
}

function KnowledgeMissing({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const href = `/Organizations/Conocimiento?id=${orgId}`;
  return (
    <PageState
      head={<PageHead title={L("ADM_EMP_TAB_KNOWLEDGE")} back={{ href, label: L("ADM_EMP_TAB_KNOWLEDGE") }} />}
      title={L("ADM_KB_MISSING")}
      line={L("ADM_KB_MISSING_LINE")}
      action={
        <a className="tm-btn secondary" href={href}>
          {L("ADM_KB_BACK")}
        </a>
      }
    />
  );
}

/**
 * Clasificación de una base o una fuente (mismo componente, E48): Audiencia (C08), Temas (C35), «Solo para estos roles»
 * (C35 con los roles de la empresa) y «Solo con estos permisos» (R8 · AR16/B10: selector de varios C08 con el catálogo de
 * permisos de Identity por su nombre; nunca un campo para escribir códigos). Envía lo que la persona ve; el servidor
 * conserva lo que no ve (B6: servicios comerciales y términos).
 */
function ClassificationForm({
  url,
  classification,
  termsHref,
  onSaved,
}: {
  url: string;
  classification: Classification;
  termsHref: string;
  onSaved: () => void;
}) {
  const { L } = useTexts();
  const [audience, setAudience] = useState(classification.audience ?? "");
  const [terms, setTerms] = useState<number[]>(classification.terms);
  const [roles, setRoles] = useState<string[]>(
    classification.roles.filter((code) => (orgRoles as readonly string[]).includes(code)),
  );
  const [permissions, setPermissions] = useState<string[]>(classification.permissions);
  const catalog = useAiData<{ items: PermissionEntry[] }>("/api/admin/catalog/permissions");
  const [error, setError] = useState<string | null>(null);
  const toggle = <T,>(list: T[], value: T, on: boolean) =>
    on ? [...list, value] : list.filter((item) => item !== value);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(url, {
      audience: audience || null,
      terms,
      shownTerms: classification.options.map((term: Term) => term.id),
      roles,
      permissions,
    });
    if (reply.status === 200 && reply.body?.saved) onSaved();
    else setError(replyMessage(L, reply.body));
  };
  const audienceOptions = [
    { value: "", label: L("ADM_KB_AUD_NONE") },
    ...audiences.map((value) => ({ value, label: L(audienceKey[value]!) })),
  ];
  return (
    <form
      id="classification"
      className={`tm-form ${ui.formGrid} ${own.formWide}`}
      noValidate
      onSubmit={(e) => void submit(e)}
    >
      {error ? (
        <div className={ui.formSummary}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}
      <div className={`tm-field ${styles.field}`}>
        <label htmlFor="cls-audience">{L("ADM_KB_AUDIENCE")}</label>
        <ChoiceMenu
          id="cls-audience"
          label={L("ADM_KB_AUDIENCE")}
          value={audience}
          options={audienceOptions}
          onChange={setAudience}
          describedBy="cls-audience-help"
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
        />
        <small id="cls-audience-help" className="tm-field-help">
          {L("ADM_KB_AUDIENCE_HELP")}
        </small>
      </div>
      <div />
      <div className={`${ui.full} ${own.group}`} role="group" aria-labelledby="cls-terms">
        <span id="cls-terms" className={own.groupTitle}>
          {L("ADM_EMP_TOPICS")}
        </span>
        {classification.options.length === 0 ? (
          <p className={ui.sectionLine}>
            {L("ADM_KB_NO_TERMS")}{" "}
            <a className="tm-link" href={termsHref}>
              {L("ADM_KB_TERMS")}
            </a>
          </p>
        ) : (
          <div className={own.checks}>
            {classification.options.map((term) => (
              <TermCheck
                key={term.id}
                term={term}
                checked={terms.includes(term.id)}
                onChange={(on) => setTerms(toggle(terms, term.id, on))}
              />
            ))}
          </div>
        )}
      </div>
      <div className={own.group} role="group" aria-labelledby="cls-roles">
        <span id="cls-roles" className={own.groupTitle}>
          {L("ADM_KB_ONLY_ROLES")} <span className="tm-hint">{L("CFG1_R1_OPTIONAL")}</span>
        </span>
        <div className={own.checks}>
          {orgRoles.map((role) => (
            <Check key={role} checked={roles.includes(role)} onChange={(on) => setRoles(toggle(roles, role, on))}>
              {L(roleKey(role))}
            </Check>
          ))}
        </div>
        <small className={own.help}>{L("ADM_KB_ONLY_ROLES_HELP")}</small>
      </div>
      <div className={`tm-field ${styles.field}`}>
        <label htmlFor="cls-permissions">
          {L("ADM_KB_ONLY_PERMISSIONS")} <span className="tm-hint">{L("CFG1_R1_OPTIONAL")}</span>
        </label>
        {catalog.state === "failed" && !catalog.data ? (
          // Sin catálogo no se puede elegir; lo guardado se conserva tal cual al guardar (nunca se borra).
          <Notice tone="error">{L("ADM_KB_PERMS_FAILED")}</Notice>
        ) : (
          <ChoiceMenu
            id="cls-permissions"
            label={L("ADM_KB_ONLY_PERMISSIONS")}
            value=""
            values={permissions}
            summary={permissionsSummary(L, permissions, catalog.data?.items ?? [])}
            options={permissionOptions(L, permissions, catalog.data?.items ?? [])}
            onChange={(code) => setPermissions(toggle(permissions, code, !permissions.includes(code)))}
            disabled={!catalog.data}
            describedBy="cls-permissions-help"
            searchLabel={L("ADM_KB_SEARCH_PERMISSION")}
            clearSearchLabel={L("ADM_AI_CLEAR_SEARCH")}
            searchIcon={<ChatIcon name="search" />}
            clearIcon={<ChatIcon name="x" />}
            chevron={<ChatIcon name="chevron-down" />}
            check={<ChatIcon name="check" />}
          />
        )}
        <small id="cls-permissions-help" className="tm-field-help">
          {L("ADM_KB_ONLY_PERMISSIONS_PICK_HELP")}
        </small>
      </div>
    </form>
  );
}

type SourceRecord = {
  organization: OrgHead;
  source: {
    id: number;
    name: string;
    description: string | null;
    type: "TEXT" | "URL" | "FILE";
    active: boolean;
    baseId: number;
    baseName: string | null;
  };
  versions: Version[] | null;
  classification: Classification | null;
};

type SourceTab = "contenido" | "versiones" | "clasificacion";

const sourceTabKey: Record<SourceTab, string> = {
  contenido: "ADM_KB_TAB_CONTENT",
  versiones: "ADM_KB_TAB_VERSIONS",
  clasificacion: "ADM_KB_CLASSIFICATION",
};

/**
 * `emp-fuente`, `emp-fuente-versiones` y `emp-fuente-clasif` · ficha de la fuente: ← a su base, tipo y estado, «{base} ·
 * versión vigente N», «Editar» (ventana `fuente`), ⋮ Desactivar…/Activar y las pestañas Contenido · Versiones ·
 * Clasificación. D15: mientras una versión se procesa, Versiones se actualiza sola cada 5 s.
 */
export function SourcePage({ orgId, sourceId, tab }: { orgId: number; sourceId: number; tab: SourceTab }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const data = useAiData<SourceRecord>(`/api/admin/organizations/${orgId}/knowledge-sources/${sourceId}`);
  const reload = () => void data.reload();
  const status = useKnowledgeStatus(orgId, "source", reload);
  const [editing, setEditing] = useState(false);
  const [versions, setVersions] = useState<{ from: Version[] | null; list: Version[] } | null>(null);
  useOpenOrg(data.data?.organization ?? null);
  const record = data.data;
  // Las versiones que llegan por la actualización automática valen mientras no cambie la lectura de la ficha.
  const shownVersions = versions && versions.from === record?.versions ? versions.list : (record?.versions ?? null);
  const processing = isProcessing(shownVersions);
  const recordVersions = record?.versions ?? null;
  useEffect(() => {
    if (!processing) return;
    const timer = window.setInterval(() => {
      void getJson<{ versions: Version[] }>(
        `/api/admin/organizations/${orgId}/knowledge-sources/${sourceId}/versions`,
      ).then((reply) => {
        if (reply.status === 200 && reply.body) setVersions({ from: recordVersions, list: reply.body.versions });
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [processing, orgId, sourceId, recordVersions]);
  useTitle(`${record?.source.name ?? L("ADM_KB_SOURCES")} · ${L(sourceTabKey[tab])}`);
  const tabs = (
    <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_KB_SOURCE_TABS")} data-part="pestanas">
      {(["contenido", "versiones", "clasificacion"] as const).map((value) => (
        <a key={value} href={sourceHref(orgId, sourceId, value)} aria-current={value === tab ? "page" : undefined}>
          {L(sourceTabKey[value])}
        </a>
      ))}
    </nav>
  );
  if (data.state === "missing") return <KnowledgeMissing orgId={orgId} />;
  if (!record) {
    const head = (
      <>
        <PageHead
          title={L("ADM_KB_SOURCES")}
          back={{ href: `/Organizations/Conocimiento?id=${orgId}`, label: L("ADM_EMP_TAB_KNOWLEDGE") }}
        />
        {tabs}
      </>
    );
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={4} table={!mobile} />
      </>
    );
  }
  const source = record.source;
  const current = shownVersions?.find((item) => item.current) ?? null;
  const sub = [
    source.baseName,
    current ? formatText(L("ADM_KB_CURRENT_VERSION"), current.number.toLocaleString(locale)) : L("ADM_KB_NO_CURRENT"),
  ]
    .filter(Boolean)
    .join(" · ");
  const menu = [
    ...(mobile ? [{ label: L("ADM_KB_EDIT"), onSelect: () => setEditing(true) }] : []),
    source.active
      ? { label: L("ADM_PPL_DEACTIVATE_MENU"), danger: true, onSelect: () => status.deactivate(source) }
      : { label: L("ADM_PPL_ACTIVATE"), onSelect: () => status.activate(source) },
  ];
  const primary =
    tab === "contenido" ? (
      <button type="submit" form="source-content" className="tm-btn primary">
        {L(source.type === "FILE" ? "ADM_KB_UPLOAD" : "ADM_KB_LOAD")}
      </button>
    ) : tab === "clasificacion" && record.classification ? (
      <button type="submit" form="classification" className="tm-btn primary">
        {L("ADM_PPL_SAVE")}
      </button>
    ) : null;
  return (
    <>
      <div className={own.ficha}>
        <PageHead
          title={source.name}
          back={{ href: baseHref(orgId, source.baseId), label: source.baseName ?? L("ADM_KB_BASE") }}
          titleTag={
            <>
              <span className={`tm-tag ${own.typeTag}`}>{L(sourceTypeKey[source.type] ?? "ADM_KB_TYPE_TEXT")}</span>
              <span className="tm-tag">{activeText(L, source.active)}</span>
            </>
          }
          sub={sub}
        >
          <RowMenu label={L("ADM_MORE_ACTIONS")} title={source.name} items={menu} />
          <button type="button" className={`tm-btn secondary ${ui.desktopInline}`} onClick={() => setEditing(true)}>
            {L("ADM_KB_EDIT")}
          </button>
          {primary ? <span className={ui.desktopInline}>{primary}</span> : null}
        </PageHead>
      </div>
      {tabs}
      {tab === "contenido" ? (
        <ContentTab
          orgId={orgId}
          source={source}
          onLoaded={(duplicate) => {
            toast.show(
              L(
                duplicate
                  ? "ADM_KB_DUPLICATE"
                  : source.type === "FILE"
                    ? "ADM_KB_FILE_RECEIVED"
                    : "ADM_KB_CONTENT_RECEIVED",
              ),
            );
            reload();
          }}
        />
      ) : tab === "versiones" ? (
        shownVersions ? (
          <VersionsTab
            orgId={orgId}
            zone={record.organization.timezone}
            source={source}
            versions={shownVersions}
            current={current}
            onChanged={reload}
          />
        ) : (
          <BlockFailed retry={data.retry} />
        )
      ) : record.classification ? (
        <ClassificationForm
          key={JSON.stringify(record.classification)}
          url={`/api/admin/organizations/${orgId}/knowledge-sources/${sourceId}/classification`}
          classification={record.classification}
          termsHref={`/Organizations/ConocimientoTerminos?id=${orgId}`}
          onSaved={() => {
            toast.show(L("ADM_KB_CLASSIFICATION_SAVED"));
            reload();
          }}
        />
      ) : (
        <BlockFailed retry={data.retry} />
      )}
      {primary ? <MobileActions>{primary}</MobileActions> : null}
      <NamedWindow
        open={editing}
        title={L("ADM_KB_EDIT_SOURCE")}
        initial={{ name: source.name, description: source.description ?? "" }}
        onClose={() => setEditing(false)}
        onSave={async (name, description) => {
          const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/knowledge-sources/${sourceId}`,
            { name, description },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(L("ADM_KB_SOURCE_SAVED"));
            reload();
            return { saved: true };
          }
          return { saved: false, error: replyMessage(L, reply.body), field: reply.body?.field === "name" };
        }}
      />
      {status.window}
    </>
  );
}

/**
 * Contenido: archivo (arrastrar o elegir), texto (se pega) o página web (se escribe la dirección), según el tipo de la
 * fuente. Al cargar: toast y la versión aparece en Versiones; un contenido que ya existía no crea otra versión.
 */
function ContentTab({
  orgId,
  source,
  onLoaded,
}: {
  orgId: number;
  source: SourceRecord["source"];
  onLoaded: (duplicate: boolean) => void;
}) {
  const { L } = useTexts();
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const empty = source.type === "FILE" ? !file : source.type === "URL" ? !url.trim() : !text.trim();
    if (empty) {
      setError(L("ADM_KB_CONTENT_EMPTY"));
      return;
    }
    setBusy(true);
    const reply =
      source.type === "FILE"
        ? await uploadFile(`/api/admin/organizations/${orgId}/knowledge-sources/${source.id}/ingest/file`, file!)
        : await postJson<{ saved?: boolean; duplicate?: boolean; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/knowledge-sources/${source.id}/ingest`,
            source.type === "URL" ? { url: url.trim() } : { text },
          );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      setFile(null);
      setText("");
      setUrl("");
      onLoaded(!!reply.body.duplicate);
      return;
    }
    setError(replyMessage(L, reply.body));
  };
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };
  return (
    <form id="source-content" noValidate onSubmit={(e) => void submit(e)} aria-busy={busy || undefined}>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {source.type === "FILE" ? (
        <>
          <div
            className={own.drop}
            data-over={over ? "true" : undefined}
            data-part="subir"
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={drop}
          >
            <span className={own.dropText}>
              <ChatIcon name="upload" />
              {L("ADM_KB_DROP")}
            </span>
            <button type="button" className={own.light} onClick={() => input.current?.click()}>
              {L("ADM_KB_PICK_FILE")}
            </button>
            <input
              ref={input}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.txt,.md,text/plain,text/markdown,application/pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>
          {file ? <p className={own.picked}>{formatText(L("ADM_KB_PICKED"), file.name)}</p> : null}
        </>
      ) : source.type === "URL" ? (
        <TextField
          id="source-url"
          label={L("ADM_KB_URL")}
          type="url"
          value={url}
          maxLength={2000}
          placeholder="https://"
          onChange={setUrl}
        />
      ) : (
        <Field id="source-text" label={L("ADM_KB_TEXT")}>
          <textarea
            id="source-text"
            className={own.textarea}
            value={text}
            maxLength={200000}
            onChange={(event) => setText(event.target.value)}
          />
        </Field>
      )}
      <p className={own.contentHelp}>{L("ADM_KB_CONTENT_HELP")}</p>
    </form>
  );
}

/** Subida multipart con el token antiforgery de la sesión (api.ts solo envía JSON). */
async function uploadFile(url: string, file: File) {
  const session = await getJson<{ antiforgeryToken: string }>("/api/admin/session");
  const body = new FormData();
  body.append("file", file, file.name);
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-TOKEN": session.body?.antiforgeryToken ?? "" },
    body,
  });
  const parsed = (await response.json().catch(() => null)) as {
    saved?: boolean;
    duplicate?: boolean;
    messageKey?: string;
  } | null;
  return { status: response.status, body: parsed };
}

/**
 * Versiones: Versión (con «Vigente») · Procesamiento · Aprobación · Fecha, con Aprobar y Rechazar en la fila (los dos
 * confirman, D8) y Reintentar para una con error. Sin botón «Actualizar» (D15).
 */
function VersionsTab({
  orgId,
  zone,
  source,
  versions,
  current,
  onChanged,
}: {
  orgId: number;
  zone: string;
  source: SourceRecord["source"];
  versions: Version[];
  current: Version | null;
  onChanged: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const [deciding, setDeciding] = useState<{ version: Version; approve: boolean } | null>(null);
  const number = (version: Version) => version.number.toLocaleString(locale);
  const approvalCell = (version: Version) =>
    version.approval === "PENDING" ? (
      version.processing === "INDEXED" ? (
        <span className="tm-tag ochre">{L("ADM_KB_APPR_PENDING")}</span>
      ) : (
        "—"
      )
    ) : (
      L(version.approval === "APPROVED" ? "ADM_KB_APPR_APPROVED" : "ADM_KB_APPR_REJECTED")
    );
  // Mientras se pide «Reintentar índice»: el botón queda ocupado (C04 cargando) y un segundo clic no sale.
  const [indexing, setIndexing] = useState<number | null>(null);
  // Un doble clic llega antes de que se dibuje el botón ocupado: la marca en una referencia corta el segundo pedido.
  const sending = useRef(false);
  const failure = (version: Version) => (version.failureKey ? L(version.failureKey) : null);
  const processingCell = (version: Version) =>
    version.processing === "FAILED" ? (
      <span className={own.procCell}>
        <span className={`tm-tag ${own.failTag}`}>{L(processingKey.FAILED!)}</span>
        {failure(version) ? <span className={own.procReason}>{failure(version)}</span> : null}
      </span>
    ) : version.processing === "RUNNING" ? (
      L(runningKey(version))
    ) : (
      L(processingKey[version.processing] ?? "ADM_KB_PROC_RUNNING")
    );
  const retryIndex = async (version: Version) => {
    if (sending.current) return;
    sending.current = true;
    setIndexing(version.id);
    try {
      const reply = await postJson<{ saved?: boolean; alreadyRunning?: boolean; messageKey?: string }>(
        `/api/admin/organizations/${orgId}/knowledge-versions/${version.id}/index/retry`,
        {},
      );
      if (reply.status === 200 && reply.body?.saved) {
        toast.show(
          formatText(L(reply.body.alreadyRunning ? "ADM_KB_INDEX_ALREADY" : "ADM_KB_INDEX_RETRIED"), number(version)),
        );
        onChanged();
      } else toast.show(replyMessage(L, reply.body));
    } finally {
      sending.current = false;
      setIndexing(null);
    }
  };
  const retry = async (version: Version) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/knowledge-versions/${version.id}/retry`,
      {},
    );
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_KB_RETRIED"), number(version)));
      onChanged();
    } else toast.show(replyMessage(L, reply.body));
  };
  const actions = (version: Version) =>
    canDecideVersion(version) ? (
      <>
        <button
          type="button"
          className={own.light}
          aria-label={formatText(L("ADM_KB_APPROVE_TITLE"), number(version))}
          onClick={() => setDeciding({ version, approve: true })}
        >
          {L("ADM_KB_APPROVE")}
        </button>
        <button
          type="button"
          className={own.light}
          aria-label={formatText(L("ADM_KB_REJECT_TITLE"), number(version))}
          onClick={() => setDeciding({ version, approve: false })}
        >
          {L("ADM_KB_REJECT")}
        </button>
      </>
    ) : canRetryVersion(version) ? (
      <button type="button" className={own.light} onClick={() => void retry(version)}>
        {L("ADM_RETRY")}
      </button>
    ) : canRetryIndex(version) ? (
      <button
        type="button"
        className={own.light}
        disabled={indexing === version.id}
        aria-busy={indexing === version.id || undefined}
        aria-label={formatText(L("ADM_KB_INDEX_RETRY_TITLE"), number(version))}
        onClick={() => void retryIndex(version)}
        data-part="reintentar-indice"
      >
        {indexing === version.id ? <span className="npa-spinner" aria-hidden="true" /> : null}
        {L("ADM_KB_INDEX_RETRY")}
      </button>
    ) : null;
  const title = (version: Version) => (
    <span className={own.version}>
      {formatText(L("ADM_KB_VERSION_N"), number(version))}
      {version.current ? <span className="tm-tag accent">{L("ADM_KB_CURRENT")}</span> : null}
    </span>
  );
  return (
    <>
      <p className={own.liveLine}>{L("ADM_KB_LIVE_LINE")}</p>
      {versions.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_KB_NO_VERSIONS")}</strong>
          <span>{L("ADM_KB_NO_VERSIONS_LINE")}</span>
          <a className="tm-btn secondary" href={sourceHref(orgId, source.id)}>
            {L("ADM_KB_TAB_CONTENT")}
          </a>
        </div>
      ) : (
        <SimpleTable
          label={L("ADM_KB_TAB_VERSIONS")}
          rows={versions}
          rowKey={(version) => version.id}
          columns={[
            { key: "version", title: L("ADM_KB_COL_VERSION"), render: title },
            { key: "processing", title: L("ADM_KB_COL_PROCESSING"), render: processingCell },
            { key: "approval", title: L("ADM_KB_COL_APPROVAL"), render: approvalCell },
            {
              key: "date",
              title: L("ADM_KB_COL_DATE"),
              render: (version) => whenIn(L, locale, version.createdAt, zone),
            },
            { key: "actions", title: "", className: own.endCell, render: (version) => actions(version) },
          ]}
          mobile={(version) => ({
            title: formatText(L("ADM_KB_VERSION_N"), number(version)),
            // Celular: Vigente, Pendiente o Falló en su etiqueta junto al título (referencia); lo demás en la línea.
            tag: version.current ? (
              <span className="tm-tag accent">{L("ADM_KB_CURRENT")}</span>
            ) : version.processing === "FAILED" ? (
              <span className={`tm-tag ${own.failTag}`}>{L("ADM_KB_PROC_FAILED")}</span>
            ) : canDecideVersion(version) ? (
              <span className="tm-tag ochre">{L("ADM_KB_APPR_PENDING")}</span>
            ) : undefined,
            line: [
              version.processing === "FAILED"
                ? failure(version)
                : version.processing === "RUNNING"
                  ? L(runningKey(version))
                  : L(processingKey[version.processing] ?? "ADM_KB_PROC_RUNNING"),
              version.approval === "PENDING"
                ? null
                : L(version.approval === "APPROVED" ? "ADM_KB_APPR_APPROVED" : "ADM_KB_APPR_REJECTED"),
              whenIn(L, locale, version.createdAt, zone),
            ]
              .filter(Boolean)
              .join(" · "),
            end: actions(version),
          })}
        />
      )}
      <ConfirmWindow
        open={!!deciding}
        danger={!deciding?.approve}
        title={formatText(
          L(deciding?.approve ? "ADM_KB_APPROVE_TITLE" : "ADM_KB_REJECT_TITLE"),
          deciding ? number(deciding.version) : "",
        )}
        line={
          deciding?.approve
            ? formatText(L("ADM_KB_APPROVE_LINE"), source.name)
            : current
              ? formatText(L("ADM_KB_REJECT_LINE"), current.number.toLocaleString(locale))
              : L("ADM_KB_REJECT_LINE_NONE")
        }
        action={L(deciding?.approve ? "ADM_KB_APPROVE" : "ADM_KB_REJECT")}
        onClose={() => setDeciding(null)}
        onConfirm={async () => {
          if (!deciding) return true;
          const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/knowledge-versions/${deciding.version.id}/approval`,
            { status: deciding.approve ? "APPROVED" : "REJECTED" },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(
              formatText(L(deciding.approve ? "ADM_KB_APPROVED" : "ADM_KB_REJECTED"), number(deciding.version)),
            );
            onChanged();
            return true;
          }
          toast.show(replyMessage(L, reply.body));
          return true;
        }}
      />
    </>
  );
}

/**
 * `emp-fuente-nueva` · formulario de página: Nombre, Base (dato de solo lectura: viene de la ficha de la base), Tipo
 * (C08 tarjetas: Texto · Página web · Archivo) y Descripción. Al crear: toast y la ficha de la fuente en Contenido (C43).
 */
export function SourceNewPage({ orgId, baseId }: { orgId: number; baseId: number }) {
  const { L } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const data = useAiData<BaseRecord>(`/api/admin/organizations/${orgId}/knowledge-bases/${baseId}`);
  const [name, setName] = useState("");
  const [type, setType] = useState<"TEXT" | "URL" | "FILE">("FILE");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(L("ADM_KB_NEW_SOURCE"));
  if (data.state === "missing") return <KnowledgeMissing orgId={orgId} />;
  const back = baseHref(orgId, baseId);
  const backLabel = data.data?.base.name ?? L("ADM_EMP_TAB_KNOWLEDGE");
  const head = <PageHead title={L("ADM_KB_NEW_SOURCE")} back={{ href: back, label: backLabel }} />;
  if (!data.data) {
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <div className={styles.formPage}>
        {head}
        <RowsSkeleton rows={3} />
      </div>
    );
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setNameError(L("ORGKBF_ERR_NAME"));
      requestAnimationFrame(() => document.getElementById("source-name")?.focus());
      return;
    }
    setBusy(true);
    setError(null);
    const reply = await postJson<{ saved?: boolean; sourceId?: number; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/knowledge-sources`,
      { baseId, type, name: name.trim(), description: description.trim() || null },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved && reply.body.sourceId) {
      toast.show(L("ADM_KB_SOURCE_CREATED"));
      navigate(sourceHref(orgId, reply.body.sourceId));
      return;
    }
    if (reply.body?.field === "name") setNameError(replyMessage(L, reply.body));
    else setError(replyMessage(L, reply.body));
  };
  const actions = (
    <>
      <a className="tm-btn secondary" href={back}>
        {L("ACTION_CANCEL")}
      </a>
      <button type="submit" form="source-new" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
        {L("ADM_KB_CREATE_SOURCE")}
      </button>
    </>
  );
  return (
    <div className={styles.formPage}>
      <PageHead title={L("ADM_KB_NEW_SOURCE")} back={{ href: back, label: backLabel }}>
        <span className={ui.desktopOnly}>{actions}</span>
      </PageHead>
      <form id="source-new" className={`tm-form ${ui.formGrid}`} noValidate onSubmit={(e) => void submit(e)}>
        {error ? (
          <div className={ui.formSummary}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}
        <TextField
          id="source-name"
          label={L("ADM_KB_NAME")}
          value={name}
          error={nameError}
          maxLength={200}
          placeholder={L("ADM_KB_SOURCE_NAME_EXAMPLE")}
          onChange={(value) => {
            setName(value);
            if (value.trim()) setNameError(null);
          }}
        />
        <div className={styles.field}>
          <span className={styles.fieldTitle}>{L("ADM_KB_BASE")}</span>
          <span className={own.readonlyValue}>{data.data.base.name}</span>
        </div>
        <div className={`${ui.full} ${styles.field}`}>
          <span className={styles.fieldTitle} id="source-type">
            {L("ADM_KB_COL_TYPE")}
          </span>
          <OptionCards
            labelledBy="source-type"
            name="type"
            value={type}
            onChange={(value) => setType(value as "TEXT" | "URL" | "FILE")}
            options={[
              { value: "TEXT", title: L("ADM_KB_TYPE_TEXT"), line: L("ADM_KB_TYPE_TEXT_LINE") },
              { value: "URL", title: L("ADM_KB_TYPE_URL"), line: L("ADM_KB_TYPE_URL_LINE") },
              { value: "FILE", title: L("ADM_KB_TYPE_FILE"), line: L("ADM_KB_TYPE_FILE_LINE") },
            ]}
          />
        </div>
        <div className={ui.full}>
          <Field
            id="source-description"
            label={L("ADM_KB_DESCRIPTION")}
            optional={L("CFG1_R1_OPTIONAL")}
            help={L("ADM_KB_SOURCE_DESCRIPTION_HELP")}
          >
            <textarea
              id="source-description"
              className={styles.textarea}
              value={description}
              maxLength={1000}
              rows={4}
              placeholder={L("ADM_KB_SOURCE_DESCRIPTION_EXAMPLE")}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
        </div>
        <div className={`${ui.mobileOnly} ${ui.full}`}>
          <FormActions>{actions}</FormActions>
        </div>
      </form>
    </div>
  );
}

type TermsList = { organization: OrgHead; items: Term[]; total: number };

const termGroups = [
  { kind: "AREA", key: "ADM_KB_AREAS" },
  { kind: "PROCESS", key: "ADM_KB_PROCESSES" },
  { kind: "TOPIC", key: "ADM_KB_TOPICS" },
] as const;

/** `emp-terminos` · Áreas, Procesos y Temas como etiquetas (C15); «Nuevo término» abre la ventana `termino`. */
export function TermsPage({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const toast = useToast();
  const data = useAiData<TermsList>(`/api/admin/organizations/${orgId}/classification/terms`);
  const [creating, setCreating] = useState(false);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(L("ADM_KB_TERMS_TITLE"));
  if (data.state === "missing") return <OrgNotFound />;
  const add = (
    <>
      <button type="button" className={`tm-btn primary ${ui.desktopInline}`} onClick={() => setCreating(true)}>
        {L("ADM_KB_NEW_TERM")}
      </button>
      <button
        type="button"
        className={ui.fab}
        aria-label={L("ADM_KB_NEW_TERM")}
        title={L("ADM_KB_NEW_TERM")}
        onClick={() => setCreating(true)}
      >
        <ChatIcon name="plus" />
      </button>
    </>
  );
  const head = (
    <PageHead
      title={L("ADM_KB_TERMS_TITLE")}
      sub={L("ADM_KB_TERMS_LINE")}
      back={{ href: `/Organizations/Conocimiento?id=${orgId}`, label: L("ADM_EMP_TAB_KNOWLEDGE") }}
    >
      {add}
    </PageHead>
  );
  const list = data.data;
  let body: ReactNode;
  if (!list) {
    body =
      data.state === "failed" ? null : (
        <div role="status" aria-busy="true" data-part="cargando">
          {termGroups.map((group) => (
            <div key={group.kind} className={own.group}>
              <span className={ui.bone} style={{ width: 120, height: 22, display: "block", marginBottom: 12 }} />
              <span className={ui.bone} style={{ width: "min(360px, 100%)", height: 28, display: "block" }} />
            </div>
          ))}
        </div>
      );
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
  } else if (list.items.length === 0) {
    body = (
      <div className="tm-empty" role="status" data-part="vacio">
        <strong>{L("ADM_KB_TERMS_EMPTY")}</strong>
        <span>{L("ADM_KB_TERMS_EMPTY_LINE")}</span>
        <button type="button" className="tm-btn primary" onClick={() => setCreating(true)}>
          {L("ADM_KB_NEW_TERM")}
        </button>
      </div>
    );
  } else {
    body = termGroups.map((group) => {
      const items = list.items.filter((term) => term.kind === group.kind);
      return (
        <section key={group.kind} aria-labelledby={`terms-${group.kind}`}>
          <h2 id={`terms-${group.kind}`} className={ui.subtitle} style={{ marginBottom: 12 }}>
            {L(group.key)}
          </h2>
          {items.length === 0 ? (
            <p className={ui.sectionLine} style={{ marginBottom: 24 }}>
              {L("ADM_KB_GROUP_EMPTY")}
            </p>
          ) : (
            <ul className={own.terms} aria-label={L(group.key)}>
              {items.map((term) => (
                <li key={term.id} className={term.active ? undefined : own.off}>
                  {term.active ? term.name : formatText(L("ADM_KB_TERM_INACTIVE"), term.name)}
                </li>
              ))}
            </ul>
          )}
        </section>
      );
    });
  }
  return (
    <>
      {head}
      {body}
      <TermWindow
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={async (kind, name) => {
          const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/classification/terms`,
            { kind, name },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(L("ADM_KB_TERM_CREATED"));
            void data.reload();
            return null;
          }
          return replyMessage(L, reply.body);
        }}
      />
    </>
  );
}

/** `termino` · Tipo (Área · Proceso · Tema, por defecto Tema) y Nombre. */
function TermWindow({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (kind: string, name: string) => Promise<string | null>;
}) {
  const { L } = useTexts();
  const [kind, setKind] = useState("TOPIC");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setKind("TOPIC");
      setName("");
      setError(null);
    }
  }
  const create = () => {
    if (!name.trim()) {
      setError(L("ORGKBF_ERR_NAME"));
      requestAnimationFrame(() => document.getElementById("term-name")?.focus());
      return;
    }
    setBusy(true);
    void onCreate(kind, name.trim()).then((failure) => {
      setBusy(false);
      if (failure) setError(failure);
      else onClose();
    });
  };
  return (
    <Window
      open={open}
      title={L("ADM_KB_NEW_TERM")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={create}
          >
            {L("ADM_KB_CREATE")}
          </button>
        </>
      }
    >
      <div className={styles.field}>
        <span className={styles.fieldTitle} id="term-kind">
          {L("ADM_KB_COL_TYPE")}
        </span>
        <div className={styles.choice} role="radiogroup" aria-labelledby="term-kind">
          {(["AREA", "PROCESS", "TOPIC"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              className="tm-pill"
              aria-checked={kind === value ? "true" : "false"}
              aria-pressed={kind === value ? "true" : "false"}
              onClick={() => setKind(value)}
            >
              {L(termKindKey[value]!)}
            </button>
          ))}
        </div>
      </div>
      <TextField
        id="term-name"
        label={L("ADM_KB_NAME")}
        value={name}
        error={error}
        maxLength={120}
        placeholder={L("ADM_KB_TERM_EXAMPLE")}
        onChange={(value) => {
          setName(value);
          if (value.trim()) setError(null);
        }}
      />
    </Window>
  );
}
